import React, { useState } from 'react';
import { validateTestAgainstLead } from '../../utils/test_validation';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions, 
  Button, 
  CircularProgress,
  Typography,
  Chip,
  Box,
  Paper,
  Badge,
  Divider
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import DatabaseIcon from '@material-ui/icons/Storage';
import WarningIcon from '@material-ui/icons/Warning';

const styles = theme => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
  validIcon: {
    color: 'green',
    marginRight: theme.spacing(1),
  },
  invalidIcon: {
    color: 'red',
    marginRight: theme.spacing(1),
  },
  warningIcon: {
    color: 'orange',
    marginRight: theme.spacing(1),
  },
  message: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  statusPaper: {
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#f5f5f5',
  },
  apiStatusSuccess: {
    color: 'green',
  },
  apiStatusError: {
    color: 'red',
  },
  apiStatusWarning: {
    color: 'orange',
  },
  openAIStatusContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  openAIStatusText: {
    marginLeft: theme.spacing(1),
    fontSize: '0.875rem',
  },
  statusContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  alertBox: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`,
  },
  databaseStatusContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
  },
  statusSummary: {
    backgroundColor: '#f8f8f8',
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #e0e0e0',
  },
  leadInfo: {
    backgroundColor: '#e3f2fd',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #bbdefb',
  }
});

/**
 * Component that validates test content against a lead
 * @param {Object} props - Component properties
 * @param {Object} props.test - The test data to validate (sections and questions)
 * @param {Object} props.lead - The lead that the test is being created for
 * @param {function} props.onClose - Function to call when dialog is closed
 * @param {function} props.onSubmit - Function to call when test is validated and submitted
 * @param {boolean} props.open - Whether the dialog is open
 */
const TestValidator = ({ classes, test, lead, onClose, onSubmit, open }) => {
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [openAIStatus, setOpenAIStatus] = useState(null);
  
  const handleValidate = async () => {
    if (!test || !lead) {
      
      return;
    }

    setValidating(true);
    try {
      
      
      // Validate the test against the lead
      const result = await validateTestAgainstLead(test, lead);
      
      
      setValidationResult(result);
      setOpenAIStatus(result.openAIStatus);
    } catch (error) {
      
      setValidationResult({
        valid: true, // Default to valid in case of error
        message: 'An error occurred during validation. Your test will still be submitted.',
        apiStatus: 'component_error',
        errorDetails: error.message,
        openAIStatus: { working: false, error: error.message }
      });
    } finally {
      setValidating(false);
    }
  };

  const handleClose = () => {
    setValidationResult(null);
    setOpenAIStatus(null);
    onClose();
  };

  const handleSubmit = () => {
    onSubmit();
    handleClose();
  };

  // Start validation when dialog opens
  React.useEffect(() => {
    if (open && test && lead && !validationResult && !validating) {
      handleValidate();
    }
  }, [open, test, lead]);

  // Determine if we should allow submission
  const shouldAllowSubmission = () => {
    if (!validationResult) return false;
    
    // If valid, allow submission
    if (validationResult.valid) return true;
    
    // If OpenAI is not available, allow submission
    if (validationResult.apiStatus === 'openai_unavailable') return true;
    
    // If there was an API error, allow submission
    if (validationResult.apiStatus && 
       (validationResult.apiStatus === 'api_error' || 
        validationResult.apiStatus === 'error' ||
        validationResult.apiStatus === 'component_error')) {
      return true;
    }
    
    return false;
  };

  // Render OpenAI status
  const renderOpenAIStatus = () => {
    if (!openAIStatus && !validationResult?.openAIStatus) return null;
    
    const status = openAIStatus || validationResult.openAIStatus;
    
    return (
      <div className={classes.openAIStatusContainer}>
        {status.working ? (
          <CheckCircleIcon style={{ color: 'green', fontSize: '1rem' }} />
        ) : (
          <CancelIcon style={{ color: 'red', fontSize: '1rem' }} />
        )}
        <Typography variant="body2" className={classes.openAIStatusText}>
          OpenAI API: {status.working ? 'Available' : 'Not Available'}
          {status.error && ` - Error: ${status.error}`}
        </Typography>
      </div>
    );
  };

  // Render lead information
  const renderLeadInfo = () => {
    if (!lead) return null;
    
    // DEBUG: Log all lead properties to see what we're getting
    
    
    
    
    
    // The primary content for validation is the transcript if available, otherwise it's the title itself.
    const isTranscriptAvailable = lead.transcript && lead.transcript.trim() !== '';
    const primaryContent = isTranscriptAvailable ? lead.transcript : lead.title;
    const contentLabel = isTranscriptAvailable ? 'Transcript' : 'Content';

    // DEBUG: Log what we're actually using
    
    
    

    return (
      <Paper className={classes.leadInfo}>
        <Typography variant="h6" style={{ marginBottom: '8px' }}>
          Testing Against Content
        </Typography>
        <Divider style={{ margin: '8px 0' }} />
        <Typography variant="body2" style={{ color: '#666', fontWeight: 'bold' }}>
          {contentLabel}:
        </Typography>
        <Typography variant="body2" style={{ marginTop: '4px', color: '#333', fontStyle: 'italic', maxHeight: '150px', overflowY: 'auto' }}>
          "{primaryContent || 'No content available'}"
        </Typography>
      </Paper>
    );
  };

  // Render validation details
  const renderValidationDetails = () => {
    if (!validationResult) return null;

    return (
      <div className={classes.chipContainer}>
        {validationResult.contentRelevance !== undefined && (
          <Chip
            icon={validationResult.contentRelevance ? <CheckCircleIcon /> : <CancelIcon />}
            label="Content Relevance"
            color={validationResult.contentRelevance ? "primary" : "secondary"}
            variant={validationResult.contentRelevance ? "default" : "outlined"}
          />
        )}
      </div>
    );
  };

  // Render API status
  const renderApiStatus = () => {
    if (!validationResult || !validationResult.apiStatus) return null;
    
    const getStatusText = (status) => {
      switch (status) {
        case 'success':
          return 'AI validation completed successfully';
        case 'success_text_analysis':
          return 'AI validation completed (text analysis)';
        case 'success_non_standard':
          return 'AI validation completed (non-standard response)';
        case 'openai_unavailable':
          return 'OpenAI API unavailable - validation skipped';
        case 'api_error':
          return 'API error during validation';
        case 'error':
          return 'Validation error occurred';
        case 'component_error':
          return 'Component error during validation';
        case 'no_answer':
          return 'No response from AI validation';
        default:
          return `Status: ${status}`;
      }
    };

    const getStatusClass = (status) => {
      switch (status) {
        case 'success':
        case 'success_text_analysis':
        case 'success_non_standard':
          return classes.apiStatusSuccess;
        case 'openai_unavailable':
        case 'no_answer':
          return classes.apiStatusWarning;
        case 'api_error':
        case 'error':
        case 'component_error':
          return classes.apiStatusError;
        default:
          return '';
      }
    };

    return (
      <Paper className={classes.statusPaper}>
        <Typography variant="body2" className={getStatusClass(validationResult.apiStatus)}>
          {getStatusText(validationResult.apiStatus)}
        </Typography>
        {renderOpenAIStatus()}
      </Paper>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      disableBackdropClick={false}
      disableEscapeKeyDown={false}
    >
      <DialogTitle className={classes.root}>
        Content Review
        <IconButton 
          aria-label="close" 
          className={classes.closeButton} 
          onClick={handleClose}
          disabled={false}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers>
        {renderLeadInfo()}
        
        {validating ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <CircularProgress />
            <Typography variant="body1" style={{ marginTop: '10px' }}>
              Checking test content against lead...
            </Typography>
          </div>
        ) : validationResult ? (
          <div>
            {renderApiStatus()}
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {validationResult.apiStatus === 'openai_unavailable' ? (
                <ErrorIcon style={{ color: 'red', marginRight: '10px' }} />
              ) : validationResult.valid ? (
                <ThumbUpIcon className={classes.validIcon} />
              ) : (
                <ThumbDownIcon className={classes.invalidIcon} />
              )}
              <Typography variant="h6">
                {validationResult.apiStatus === 'openai_unavailable'
                  ? 'Test validation is not available'
                  : validationResult.valid 
                    ? 'Your test content aligns with the lead!' 
                    : 'Your test content needs review.'}
              </Typography>
            </div>
            
            {validationResult.apiStatus !== 'openai_unavailable' && (
              <div>
                <DialogContentText className={classes.message}>
                  {validationResult.message}
                </DialogContentText>
                
                {renderValidationDetails()}
              </div>
            )}
            
            <Divider className={classes.divider} />
            
            {validationResult.errorDetails && (
              <Paper className={classes.alertBox} style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', padding: '16px' }}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold', color: '#856404', marginBottom: '8px' }}>
                  Validation Details
                </Typography>
                <Typography variant="body2" style={{ color: '#856404' }}>
                  {validationResult.errorDetails}
                </Typography>
              </Paper>
            )}
          </div>
        ) : null}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={validating}>
          Cancel
        </Button>
        {validationResult && shouldAllowSubmission() && (
          <Button 
            onClick={handleSubmit} 
            color="primary" 
            variant="contained"
            disabled={validating}
          >
            {validationResult.valid ? 'SUBMIT TEST' : 'SUBMIT ANYWAY'}
          </Button>
        )}
        {validationResult && !shouldAllowSubmission() && (
          <Button 
            onClick={handleClose} 
            color="primary" 
            variant="contained"
            disabled={validating}
          >
            Go Back
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(styles)(TestValidator); 